/**
 * First-minute coaching beats + progressive help copy.
 * Sim owns step transitions; presentation reads help via these helpers.
 */

export const ONBOARDING_STEP = {
  MOVE: 0,
  BOOST: 1,
  AIM_FIRE: 2,
  QF_LITERACY: 3,
  CARRIER_HOME: 4,
  DONE: 5,
} as const;

/** Soft gate: encourage Q/F once, then advance even if unused. */
export const ONBOARDING_SOFT_GATE_S = 18;

export type CoachPulse = "arc_torp" | null;

export const ONBOARDING_MESSAGES: Record<number, string> = {
  [ONBOARDING_STEP.MOVE]: "Move with WASD / sticks — get airborne.",
  [ONBOARDING_STEP.BOOST]: "Boost through danger — Shift, A, or the Boost button.",
  [ONBOARDING_STEP.AIM_FIRE]:
    "Aim and fire — mouse click or aim stick. Crate east of the CARRIER pad.",
  [ONBOARDING_STEP.QF_LITERACY]: "Q Arc · F Torp — fire secondary or ordnance once.",
  [ONBOARDING_STEP.CARRIER_HOME]:
    "CARRIER pad is home — follow the compass and hold in the extract ring.",
};

/** Progressive one-liners for the help strip (not the full binding wall). */
export const ONBOARDING_HELP_LINES: Record<number, string> = {
  [ONBOARDING_STEP.MOVE]: "WASD / sticks — move",
  [ONBOARDING_STEP.BOOST]: "Shift / A · Boost — break through danger",
  [ONBOARDING_STEP.AIM_FIRE]: "Mouse / aim stick — fire primary",
  [ONBOARDING_STEP.QF_LITERACY]: "Q Arc · F Torp — second weapons",
  [ONBOARDING_STEP.CARRIER_HOME]: "E / Launch on the lit CARRIER pad — extract home",
  [ONBOARDING_STEP.DONE]: "E extract · P pause · M mute",
};

export const COLLAPSED_HELP = "E extract · P pause · M mute · R relaunch";

export function onboardingMessage(step: number): string {
  return ONBOARDING_MESSAGES[step] ?? ONBOARDING_MESSAGES[ONBOARDING_STEP.MOVE]!;
}

export function shouldCollapseBindings(args: {
  runsCompleted: number;
  weaponLiteracyCoached: boolean;
}): boolean {
  return args.runsCompleted >= 1 || args.weaponLiteracyCoached;
}

export function progressiveHelpLine(args: {
  runsCompleted: number;
  weaponLiteracyCoached: boolean;
  onboardingStep: number;
  phase: string;
}): string {
  if (shouldCollapseBindings(args)) {
    return COLLAPSED_HELP;
  }
  if (args.phase === "carrier" || args.phase === "extracted" || args.phase === "dead") {
    return "E / Launch — begin expedition · craft in the bay";
  }
  const step = Math.max(0, Math.min(ONBOARDING_STEP.DONE, args.onboardingStep));
  return ONBOARDING_HELP_LINES[step] ?? ONBOARDING_HELP_LINES[ONBOARDING_STEP.MOVE]!;
}
